import type {
  ImageProps,
  SystemProps,
  SystemStyleObject,
} from '@hover/blueprint';
import {
  cssVariables,
  mapResponsive,
  transition,
  useTheme,
} from '@hover/blueprint';
import type { ResponsiveValue } from '@hover/blueprint/chakra';
import type { ColorScheme } from '@hover/blueprint/foundation';
import { resetFocus } from '@hover/blueprint/foundation';
import { includes } from '@hover/utilities';

type NudgeSize = Parameters<(typeof cssVariables)['space']>[0];
type Direction = 'left' | 'right' | 'up' | 'down';

/**
 * Nudge an element using system spacing values. This uses `transform` so be
 * aware it will override existing transforms applied to an element.
 *
 * @param distance distance to nudge as space token
 * @param direction direction to nudge
 *
 * @returns system props to spread into element
 */
export const nudge = (
  distance: ResponsiveValue<NudgeSize>,
  direction: Direction | [Direction, Direction],
) => {
  const directions = typeof direction === 'string' ? [direction] : direction;

  return {
    transform: mapResponsive(
      space =>
        directions
          .map(d => {
            const axis = includes(['left', 'right'], d) ? 'X' : 'Y';
            const movement = includes(['left', 'up'], d) ? '-1' : '1';

            return `translate${axis}(calc(${
              cssVariables.space(space).reference
            } * ${movement}))`;
          })
          .join(' '),
      distance,
    ),
  } satisfies SystemProps;
};

/**
 * Apply styles to image `alt` text
 */
export const altStyle = (textStyle: ImageProps['textStyle'] = 'body500') =>
  ({
    color: 'transparent',
    _before: {
      textStyle,
      color: 'darkAlpha.300',
      fontFamily: 'body',
      top: '50%',
      left: '50%',
      position: 'absolute',
      transform: 'translate(-50%, -50%)',
    },
  } satisfies Partial<ImageProps>);

export interface LightBorderOptions {
  /**
   * Enable this if we're using the `ghost` variant on mobile
   */
  ghostForMobile?: boolean;
}

/**
 * Subtle modifications to the `tertiary` button variant to match the designs in
 * a few places
 *
 * @todo(design-system) this should probably be incorporated into the
 * design system as a modification of the tertiary button variant or
 * as another variant or option. This modifies just the idle state
 * to match the radio and check buttons and the designs.
 */
export const tertiaryButtonLightBorder = ({
  ghostForMobile = false,
}: LightBorderOptions = {}) => {
  const disabledColor = 'darkAlpha.200 !important';
  return {
    borderColor: {
      base: ghostForMobile ? 'transparent' : undefined,
      tablet: 'darkAlpha.400',
    },
    _hover: {
      borderColor: {
        base: ghostForMobile ? undefined : 'currentColor',
        tablet: 'currentColor',
      },
    },
    _disabled: {
      color: {
        base: ghostForMobile ? 'darkAlpha.300 !important' : disabledColor,
        tablet: disabledColor,
      },
      borderColor: {
        base: ghostForMobile ? undefined : disabledColor,
        tablet: disabledColor,
      },
      _after: { display: 'none' },
    },
  };
};

export const useImageFocus = (
  selector: string = 'img',
  thickness: string = '5px',
): SystemStyleObject => {
  const { colors } = useTheme();
  const childImage = `& ${selector}`;

  return {
    ...resetFocus(),
    [childImage]: {
      outline: `${thickness} solid transparent`,
      outlineOffset: `-${thickness}`,
      ...transition.easing250('outline'),
    },
    _focusVisible: {
      [childImage]: {
        outlineColor: colors.alpha(0.5, 'neutral.0'),
      },
    },
  };
};

/**
 * Apply styles only when the user's device supports `:hover` states
 *
 * @param styles style object to apply
 * @returns style object wrapped in `hover` media query
 */
export const withHover = (styles: SystemStyleObject) => ({
  '@media (hover: hover)': styles,
});

/**
 * Glass button style overrides
 *
 * @todo move this to the design system as a `variant` or `colorScheme` in the
 * button theme
 *
 * @param colorScheme light or dark color scheme, defaults to `dark` if anything
 * else is passed
 *
 * @returns style object to be passed via `sx`
 */
export const glass = (colorScheme?: ColorScheme) => {
  const active = {
    _active: {
      background: `${
        colorScheme === 'light' ? 'lightAlpha.200' : 'darkAlpha.200'
      } !important`,
      ...withHover({
        background: `${
          colorScheme === 'light' ? 'lightAlpha.300' : 'darkAlpha.300'
        } !important`,
      }),
    },
  };

  return {
    backdropFilter: 'blur(30px)',
    ...withHover({
      _hover: {
        background: `${
          colorScheme === 'light' ? 'lightAlpha.400' : 'darkAlpha.400'
        } !important`,
      },
      ...active,
    }),
    ...active,
    background: colorScheme === 'light' ? 'lightAlpha.300' : 'darkAlpha.300',
  };
};

/**
 * Disable the `translate` slide up animation on the loader in `Button` so only
 * opacity is animated
 *
 * @todo I think we should disable the translation for the `/next` theme in the
 * system, but we'll wait to see what design comes up with here
 */
export const disableLoaderSlide = {
  '& > div': { transform: 'unset !important' },
};
